import Image from "next/image";

import { ChevronDown, PhoneIcon } from "lucide-react";

import { IconStar } from "@/components/icons";
import CalendarBookingLink from "@/components/seo/CalendarBookingLink";
import { Accordion, AccordionContent, AccordionItem, AccordionPrimitive } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const CustomerAvatars = () => {
  const logoUrls = [
    "https://ik.imagekit.io/4wu305uo4/image_6736429de375273f605d1bba.webp",
    "https://ik.imagekit.io/4wu305uo4/image_67363b97e375273f603636ce.png",
    "https://ik.imagekit.io/4wu305uo4/customized-sections/premium-roofing-systems-logo.png",
    "https://ik.imagekit.io/4wu305uo4/customized-sections/K2-Logo-2.svg",
    "https://ik.imagekit.io/4wu305uo4/customized-sections/Ingos-Logo-2.svg",
    "https://ik.imagekit.io/4wu305uo4/image_67363d9be375273f604509e8.webp",
  ];
  return (
    <div className="flex -space-x-[18px]" role="group" aria-label="Customer logos">
      {logoUrls.map((url, i) => (
        <div key={i} className="relative h-10 w-10 p-1 rounded-full bg-white shadow-sm flex items-center justify-center">
          <Image
            src={url}
            alt={`Customer logo ${i + 1}`}
            width={32}
            height={32}
            priority
            loading="eager"
            className="rounded-full object-contain h-full w-full"
          />
        </div>
      ))}
    </div>
  );
};

const MCBadge = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn("flex items-center justify-center w-10 h-10 rounded-full text-black relative shrink-0", className)}
    >
      {/* rebolt check mark badge */}
      <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        className="absolute -bottom-[2px] -right-[5px]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="4" width="8.5" height="8.5" fill="white" />

        <path
          d="M14.1163 6.42625C13.8806 6.18 13.6369 5.92625 13.545 5.70312C13.46 5.49875 13.455 5.16 13.45 4.83187C13.4406 4.22187 13.4306 3.53062 12.95 3.05C12.4694 2.56937 11.7781 2.55937 11.1681 2.55C10.84 2.545 10.5013 2.54 10.2969 2.455C10.0744 2.36312 9.82 2.11937 9.57375 1.88375C9.1425 1.46937 8.6525 1 8 1C7.3475 1 6.85812 1.46937 6.42625 1.88375C6.18 2.11937 5.92625 2.36312 5.70312 2.455C5.5 2.54 5.16 2.545 4.83187 2.55C4.22187 2.55937 3.53062 2.56937 3.05 3.05C2.56937 3.53062 2.5625 4.22187 2.55 4.83187C2.545 5.16 2.54 5.49875 2.455 5.70312C2.36312 5.92562 2.11937 6.18 1.88375 6.42625C1.46937 6.8575 1 7.3475 1 8C1 8.6525 1.46937 9.14187 1.88375 9.57375C2.11937 9.82 2.36312 10.0737 2.455 10.2969C2.54 10.5013 2.545 10.84 2.55 11.1681C2.55937 11.7781 2.56937 12.4694 3.05 12.95C3.53062 13.4306 4.22187 13.4406 4.83187 13.45C5.16 13.455 5.49875 13.46 5.70312 13.545C5.92562 13.6369 6.18 13.8806 6.42625 14.1163C6.8575 14.5306 7.3475 15 8 15C8.6525 15 9.14187 14.5306 9.57375 14.1163C9.82 13.8806 10.0737 13.6369 10.2969 13.545C10.5013 13.46 10.84 13.455 11.1681 13.45C11.7781 13.4406 12.4694 13.4306 12.95 12.95C13.4306 12.4694 13.4406 11.7781 13.45 11.1681C13.455 10.84 13.46 10.5013 13.545 10.2969C13.6369 10.0744 13.8806 9.82 14.1163 9.57375C14.5306 9.1425 15 8.6525 15 8C15 7.3475 14.5306 6.85812 14.1163 6.42625ZM10.8538 6.85375L7.35375 10.3538C7.30731 10.4002 7.25217 10.4371 7.19147 10.4623C7.13077 10.4874 7.06571 10.5004 7 10.5004C6.93429 10.5004 6.86923 10.4874 6.80853 10.4623C6.74783 10.4371 6.69269 10.4002 6.64625 10.3538L5.14625 8.85375C5.05243 8.75993 4.99972 8.63268 4.99972 8.5C4.99972 8.36732 5.05243 8.24007 5.14625 8.14625C5.24007 8.05243 5.36732 7.99972 5.5 7.99972C5.63268 7.99972 5.75993 8.05243 5.85375 8.14625L7 9.29313L10.1462 6.14625C10.1927 6.09979 10.2479 6.06294 10.3086 6.0378C10.3692 6.01266 10.4343 5.99972 10.5 5.99972C10.5657 5.99972 10.6308 6.01266 10.6914 6.0378C10.7521 6.06294 10.8073 6.09979 10.8538 6.14625C10.9002 6.1927 10.9371 6.24786 10.9622 6.30855C10.9873 6.36925 11.0003 6.4343 11.0003 6.5C11.0003 6.5657 10.9873 6.63075 10.9622 6.69145C10.9371 6.75214 10.9002 6.8073 10.8538 6.85375Z"
          fill="#CC4400"
        />
      </svg>

      <span className="text-sm font-semibold">MC</span>
    </div>
  );
};

const BusinessProfileCard = ({
  industry,
  isIndustrySpecificPage,
}: {
  industry: string;
  isIndustrySpecificPage: boolean;
}) => {
  return (
    <div className="hidden sm:block absolute right-3 lg:-right-12 z-5 lg:top-8 -top-10 rounded-[20px] p-5 lg:flex flex-col bg-ash-100 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.07)]">
      <div className="flex items-center space-x-4 pb-4 border-b border-ash-200 mb-4">
        <MCBadge className={isIndustrySpecificPage ? "bg-[#CBFF81]" : "bg-honey-300"} />

        <div className="flex-1">
          <div className="flex flex-col space-y-0.5">
            <span className="text-ash-800 font-semibold">My {industry} Company</span>
            <p className="text-xs text-ash-800/55">Google Business Profile</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center space-x-1.5">
        <div className="flex flex-col space-y-1.5 flex-1">
          <div className="h-2 rounded-sm w-5/6 bg-ash-200" />
          <div className="h-2 rounded-sm w-5/6 bg-ash-200" />
          <div className="h-2 rounded-sm w-4/6 bg-ash-200" />
          <div className="h-2 rounded-sm w-3/6 bg-ash-200" />
        </div>
        <Button
          variant="outline"
          disabled
          className="flex bg-transparent items-center space-x-1.5 border-rebolt-600 text-rebolt-600 pointer-events-none"
        >
          <PhoneIcon className="w-4 h-4" />
          <span>Call now</span>
        </Button>
      </div>
    </div>
  );
};

function Blurb({ blurb }: { blurb: string }) {
  if (!blurb) return null;
  const splitBlurb = blurb.replace(/\n+/g, "\n").split("\n");

  const paragraphs = splitBlurb.map((paragraph, i) => <p key={i}>{paragraph}</p>);

  return <>{paragraphs}</>;
}

function PageHero({
  industry,
  industrySlug,
  city,
  stateAbbr,
  primaryImageUrl,
  cityBlurb = "",
  industryBlurb = "",
}: {
  industry: string;
  industrySlug: string;
  city?: string;
  stateAbbr?: string;
  primaryImageUrl: string;
  cityBlurb?: string;
  industryBlurb?: string;
}) {
  const isIndustrySpecificPage = !city && !stateAbbr;
  const teamImage = `https://ik.imagekit.io/4wu305uo4/${industrySlug}/team/${industrySlug}3.png`;

  const headlineText = isIndustrySpecificPage
    ? `${industry} Web Design Company`
    : `${city}, ${stateAbbr} ${industry} Web Design`;

  const readMoreTextBlurb = cityBlurb || industryBlurb; // city pages will have a cityBlurb, industry pages will have an industryBlurb

  return (
    <section className="bg-ash-100 w-full">
      <div className="container mx-auto pb-16 lg:pt-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-11 gap-8">
          <div className="col-span-1 lg:col-span-5 mt-12 mb-14 lg:mb-0">
            <h1 className="text-4xl lg:text-5xl font-bold lg:leading-[60px] mb-6">{headlineText}</h1>
            <p className="text-ash-700 pr-6 sm:pr-16 text-lg mb-6">
              We&apos;ll build your website for free. Pay only if you love it.
            </p>

            <article className="flex items-center space-x-3 mb-6" aria-label="Customer reviews">
              <CustomerAvatars />

              <div className="flex flex-col items-start space-y-1.5">
                <div
                  className="flex items-center gap-0.5 text-honey-400"
                  role="group"
                  aria-label="Rating: 4.9 out of 5 stars"
                >
                  {[...Array(5)].map((_, i) => (
                    <IconStar key={i} className="w-4 h-4 aspect-square" />
                  ))}
                  <span className="text-sm ml-1 mt-px text-ash-800 font-bold">4.9</span>
                </div>
                <p className="text-ash-800 text-xs">Trusted by over 300 businesses</p>
              </div>
            </article>

            <div className="relative mb-2">
              <Accordion type="single" collapsible>
                <AccordionItem value="read-more" className="border-0">
                  <AccordionPrimitive.Header>
                    <AccordionPrimitive.Trigger className="group flex w-full items-center py-3 text-left">
                      <span className="text-base font-medium text-ash-900">Read more</span>
                      <ChevronDown className="ml-2 h-4 w-4 text-ash-500 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionContent forceMount className="text-ash-600 leading-relaxed prose">
                    <p className="pt-2">
                      We build websites that turn visitors into leads, delivering exactly what your business needs.
                    </p>
                    <Blurb blurb={readMoreTextBlurb} />
                    <p className="">
                      With years of proven experience creating websites that perform, we&apos;ve become a trusted name
                      in {city ? `${city}'s` : `the ${industry} industry's`} digital landscape. Our team doesn&apos;t
                      just build websites, we create digital storefronts that work as hard as you do.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <CalendarBookingLink className="mt-2" />
          </div>

          <div className="relative col-span-1 lg:col-span-6 h-full w-full md:mx-auto">
            <BusinessProfileCard industry={industry} isIndustrySpecificPage={isIndustrySpecificPage} />

            <div className="overflow-hidden relative rounded-[20px]">
              <div className="h-full min-w-full min-h-[550px] brightness-75 blur-[2px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.07)] relative">
                <Image
                  src={primaryImageUrl}
                  alt={isIndustrySpecificPage ? `Image of completed ${industry} job` : `Image of ${city}, ${stateAbbr}`}
                  priority
                  loading="eager"
                  fill
                  sizes="700px"
                  className="rounded-[20px] object-cover"
                />
              </div>
              <div className="absolute bottom-0 left-3 sm:left-16 w-[265px] sm:w-80 translate-y-16 sm:translate-y-7 lg:-translate-x-8 ">
                <div
                  className={cn("flex flex-nowrap space-x-3 items-center", isIndustrySpecificPage ? "mb-4" : "mb-2")}
                >
                  <MCBadge className={isIndustrySpecificPage ? "bg-[#CBFF81]" : "bg-honey-300"} />
                  <div className="flex flex-col w-full text-white space-y-0.5">
                    <span className="text-sm leading-tight w-3/5">My {industry} Company Website</span>
                    {!isIndustrySpecificPage && (
                      <p className="text-xs font-normal">
                        {city}, {stateAbbr}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-ash-200 rounded-t-lg p-1">
                  <div
                    className={cn(
                      "flex items-center rounded-t-lg space-x-4 p-4",
                      isIndustrySpecificPage ? "bg-[#CBFF81]" : "bg-honey-300"
                    )}
                  >
                    <div className="flex flex-col space-y-1.5">
                      <span className="font-bold text-ash-800 mb-1 leading-tight">My {industry} Company</span>
                      <div className="flex flex-col space-y-1.5 flex-1">
                        <div className="h-1.5 rounded-sm w-5/6 bg-ash-600/20" />
                        <div className="h-1.5 rounded-sm w-5/6 bg-ash-600/20" />
                        <div className="h-1.5 rounded-sm w-4/6 bg-ash-600/20" />
                        <div className="h-1.5 rounded-sm w-3/6 bg-ash-600/20" />
                      </div>
                    </div>

                    <div className="h-28 w-28 relative shrink-0 rounded-lg">
                      <Image
                        fill
                        quality={75}
                        priority
                        loading="eager"
                        alt={`${industry} team`}
                        sizes="112px"
                        className="object-cover rounded-lg brightness-[0.9]"
                        src={teamImage}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col space-y-1.5 p-4 -mb-5 w-32">
                    <div className="h-1.5 rounded-xs w-2/6 bg-ash-600/20" />
                    <div className="h-1.5 rounded-xs w-5/6 bg-ash-600/20" />
                  </div>

                  <div className="grid grid-cols-3 gap-x-1 w-full p-4 pb-1">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex flex-col space-y-0.5 border rounded-sm p-2 border-ash-600/10">
                        <span className="flex items-center space-x-1 mb-1">
                          <div className="w-2 h-2 bg-ash-600/30" />
                          <div className="h-2 rounded-xs w-full bg-ash-600/20" />
                        </span>
                        <div className="h-1 rounded-xs w-full bg-ash-600/20" />
                        <div className="h-1 rounded-xs w-full bg-ash-600/20" />
                        <div className="h-1 rounded-xs w-4/6 bg-ash-600/20" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PageHero;
